const GEO_PHONE_KEY = 'volksign_geo_phone_v2';

async function detectGeoFromIpwho() {
  const response = await fetch('https://ipwho.is/');
  const data = await response.json();
  if (!data.success) {
    throw new Error(data.message || 'ipwho lookup failed');
  }
  const callingCode =
    data.calling_code != null && data.calling_code !== ''
      ? `+${String(data.calling_code).replace(/^\+/, '')}`
      : null;
  return {
    iso2: data.country_code ? String(data.country_code).toUpperCase() : null,
    countryName: data.country || null,
    callingCode,
  };
}

async function detectGeoFromIpapi() {
  const response = await fetch('https://ipapi.co/json/', {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`ipapi status ${response.status}`);
  }
  const data = await response.json();
  if (data.error) {
    throw new Error(data.reason || 'ipapi error');
  }
  return {
    iso2: data.country_code ? String(data.country_code).toUpperCase() : null,
    countryName: data.country_name || null,
    callingCode: data.country_calling_code || null,
  };
}

/** Cloudflare trace — ISO only, no API key. */
async function detectIsoFromCloudflare() {
  const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace');
  const text = await response.text();
  const locLine = text.split('\n').find((line) => line.startsWith('loc='));
  const iso = locLine?.slice(4)?.trim();
  return iso && iso.length === 2 ? iso.toUpperCase() : null;
}

/** Browser timezone hint when IP APIs fail (e.g. Asia/Karachi → PK). */
export function detectIsoFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const TZ_TO_ISO = {
      'Asia/Karachi': 'PK',
      'Asia/Kolkata': 'IN',
      'Asia/Dhaka': 'BD',
      'Asia/Colombo': 'LK',
      'Asia/Kathmandu': 'NP',
      'Asia/Dubai': 'AE',
      'Asia/Riyadh': 'SA',
      'Asia/Singapore': 'SG',
      'Asia/Kuala_Lumpur': 'MY',
      'Asia/Jakarta': 'ID',
      'Asia/Manila': 'PH',
      'Asia/Bangkok': 'TH',
      'Asia/Ho_Chi_Minh': 'VN',
      'Asia/Tokyo': 'JP',
      'Asia/Seoul': 'KR',
      'Asia/Shanghai': 'CN',
      'Asia/Hong_Kong': 'HK',
      'Australia/Sydney': 'AU',
      'Australia/Melbourne': 'AU',
      'Australia/Brisbane': 'AU',
      'Australia/Perth': 'AU',
      'Europe/London': 'GB',
      'America/New_York': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      'America/Toronto': 'CA',
    };
    return TZ_TO_ISO[tz] || null;
  } catch {
    return null;
  }
}

/**
 * Detect visitor country for phone default. Cached per browser session.
 */
export async function detectGeoPhoneCountry() {
  try {
    const cached = sessionStorage.getItem(GEO_PHONE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.iso2) return parsed;
    }
  } catch {
    sessionStorage.removeItem(GEO_PHONE_KEY);
  }

  const providers = [
    { name: 'ipwho', fn: detectGeoFromIpwho },
    { name: 'ipapi', fn: detectGeoFromIpapi },
  ];

  let geo = null;

  for (const provider of providers) {
    try {
      geo = await provider.fn();
      if (geo?.iso2) break;
    } catch (error) {
      console.warn(`${provider.name} geo lookup failed:`, error?.message || error);
    }
  }

  if (!geo?.iso2) {
    try {
      const iso = await detectIsoFromCloudflare();
      if (iso) {
        geo = { iso2: iso, countryName: null, callingCode: null };
      }
    } catch (error) {
      console.warn('Cloudflare geo trace failed:', error?.message || error);
    }
  }

  if (!geo?.iso2) {
    const tzIso = detectIsoFromTimezone();
    if (tzIso) {
      geo = { iso2: tzIso, countryName: null, callingCode: null };
    }
  }

  if (geo?.iso2) {
    try {
      sessionStorage.setItem(GEO_PHONE_KEY, JSON.stringify(geo));
    } catch {
      /* ignore */
    }
  }

  return geo;
}

/** Clear cached geo (e.g. after user reports wrong country). */
export function clearGeoPhoneCache() {
  try {
    sessionStorage.removeItem(GEO_PHONE_KEY);
  } catch {
    /* ignore */
  }
}
